<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                :title="
                    $t('forms.title', {
                        prefix: titlePrefix,
                        resource: $tc('resources.product.name', 1),
                    })
                "
            />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-text-field
                            v-model="product.name"
                            :label="$t('resources.product.attributes.name')"
                            autofocus
                            :error-messages="nameErrors"
                            @input="$v.product.name.$touch()"
                            @blur="$v.product.name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-currency-field
                            :label="$t('resources.product.attributes.price')"
                            v-bind="$t('money-config')"
                            v-model="product.price"
                            :error-messages="priceErrors"
                            @blur="$v.product.price.$touch()"
                        ></v-currency-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-currency-field
                            :label="
                                $t('resources.product.attributes.installments')
                            "
                            v-bind="$t('int-config')"
                            v-model="product.installments"
                            :error-messages="installmentsErrors"
                            @blur="$v.product.installments.$touch()"
                        ></v-currency-field>
                    </v-col>
                </v-row>
            </v-card-text>
            <FormActions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import BaseModel from '@/models/BaseModel'
import { validationMixin } from 'vuelidate'
import { required, between, minValue } from 'vuelidate/lib/validators'

export default {
    components: {
        CardHeader,
        FormActions,
    },

    mixins: [validationMixin],

    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        titlePrefix: String,
    },

    data: () => {
        return {
            loading: false,
        }
    },

    validations() {
        return {
            product: {
                name: { required },
                price: { required, minValue: minValue(1) },
                installments: { required, between: between(1, 12) },
            },
        }
    },

    computed: {
        product() {
            return this.model
        },

        nameErrors() {
            const errors = []
            if (!this.$v.product.name.$dirty) return errors

            !this.$v.product.name.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.product.attributes.name'),
                    }),
                )

            return errors
        },

        priceErrors() {
            const errors = []
            if (!this.$v.product.price.$dirty) return errors

            !this.$v.product.price.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t('resources.product.attributes.price'),
                    }),
                )

            !this.$v.product.price.minValue &&
                errors.push(
                    this.$t('forms.errors.minValue', {
                        label: this.$t('resources.product.attributes.price'),
                        min: 1,
                    }),
                )

            return errors
        },

        installmentsErrors() {
            const errors = []
            if (!this.$v.product.installments.$dirty) return errors

            !this.$v.product.installments.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.product.attributes.installments',
                        ),
                    }),
                )

            !this.$v.product.installments.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: this.$t(
                            'resources.product.attributes.installments',
                        ),
                        min: 1,
                        max: 12,
                    }),
                )

            return errors
        },
    },

    methods: {
        async save() {
            this.loading = true
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.model.clone()
            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return
            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.product.name',
                        1,
                    ).toLowerCase(),
                    name: clone.name,
                }),
            )
        },
    },
}
</script>

<style></style>
